import { colors } from './theme';
import { parseDateInput } from './utils/dates';

// Months before warranty expiry when the status enters the "warning" state.
export const WARRANTY_WARNING_MONTHS = 12;

// Extra months added when a device has been claimed to an org.
export const WARRANTY_CLAIM_EXTRA_MONTHS = 6;

function formatCountdown(expiry, now = new Date()) {
  const diffDays = Math.ceil(Math.abs(expiry - now) / (1000 * 60 * 60 * 24));
  if (diffDays >= 30) {
    const v = Math.floor(diffDays / 30);
    return `${v} ${v === 1 ? 'month' : 'months'}`;
  }
  if (diffDays >= 7) {
    const v = Math.floor(diffDays / 7);
    return `${v} ${v === 1 ? 'week' : 'weeks'}`;
  }
  return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
}

// Returns a structured breakdown of all warranty tiers and their countdowns.
// Warranty is additive: family base + company-specific + claim + org agreement.
// companyExtraMonths: additive months from the device's company (e.g. SAAB)
// claimExtraMonths:   +6 months from claiming the device to an org
// orgExtraMonths:     extra months from a special org agreement
export function getWarrantyBreakdown(scandate, claimExtraMonths = 0, orgExtraMonths = 0, baseMonths = 36, companyExtraMonths = 0) {
  if (!scandate) return null;
  const parsed = parseDateInput(scandate);
  if (!parsed) return null;

  const now = new Date();

  // The "base" tier covers the family base period plus any company-specific
  // months, since both are part of the standard contract for this product.
  const baseExpiry = new Date(parsed);
  baseExpiry.setMonth(baseExpiry.getMonth() + baseMonths + companyExtraMonths);

  const claimExpiry = new Date(baseExpiry);
  claimExpiry.setMonth(claimExpiry.getMonth() + claimExtraMonths);

  const totalExpiry = new Date(claimExpiry);
  totalExpiry.setMonth(totalExpiry.getMonth() + orgExtraMonths);

  const inBase     = now < baseExpiry;
  const inClaim    = !inBase && claimExtraMonths > 0 && now < claimExpiry;
  const inOrg      = !inBase && !inClaim && orgExtraMonths > 0 && now < totalExpiry;
  const inExtended = !inBase && now < totalExpiry;
  const expired    = now >= totalExpiry;

  return {
    baseExpiry,
    claimExpiry: claimExtraMonths > 0 ? claimExpiry : null,
    totalExpiry,
    inBase,
    inClaim,
    inOrg,
    inExtended,
    expired,
    baseMonths,
    companyExtraMonths,
    claimExtraMonths,
    orgExtraMonths,
    baseCountdown:  inBase                              ? formatCountdown(baseExpiry,  now) : null,
    claimCountdown: claimExtraMonths > 0 && !expired   ? formatCountdown(claimExpiry, now) : null,
    totalCountdown: !expired                            ? formatCountdown(totalExpiry, now) : null,
    expiredAgo:     expired                             ? formatCountdown(totalExpiry, now) : null,
  };
}

export function getWarrantyStatus(scandate, warningMonths = WARRANTY_WARNING_MONTHS, claimExtraMonths = 0, orgExtraMonths = 0, baseMonths = 36, companyExtraMonths = 0) {
  if (!scandate) return 'unknown';
  const parsed = parseDateInput(scandate);
  if (!parsed) return 'unknown';

  const expiry = new Date(parsed);
  expiry.setMonth(expiry.getMonth() + baseMonths + companyExtraMonths + claimExtraMonths + orgExtraMonths);

  const now = new Date();
  if (now > expiry) return 'outside';

  const warningDate = new Date(expiry);
  warningDate.setMonth(warningDate.getMonth() - warningMonths);
  if (now >= warningDate) return 'warning';

  return 'within';
}

export function getWarrantyExpiry(scandate, claimExtraMonths = 0, orgExtraMonths = 0, baseMonths = 36, companyExtraMonths = 0) {
  if (!scandate) return null;
  const parsed = parseDateInput(scandate);
  if (!parsed) return null;
  const expiry = new Date(parsed);
  expiry.setMonth(expiry.getMonth() + baseMonths + companyExtraMonths + claimExtraMonths + orgExtraMonths);
  return expiry;
}

function baseWarrantyLabel(baseMonths) {
  if (baseMonths % 12 === 0) {
    const y = baseMonths / 12;
    return `${y}-year`;
  }
  return `${baseMonths}-month`;
}

export function getWarrantyTimeMessage(scandate, claimExtraMonths = 0, orgExtraMonths = 0, baseMonths = 36, companyExtraMonths = 0) {
  const bd = getWarrantyBreakdown(scandate, claimExtraMonths, orgExtraMonths, baseMonths, companyExtraMonths);
  if (!bd) return 'Warranty information unavailable';

  if (bd.expired) return `Warranty expired ${bd.expiredAgo} ago`;

  const label = baseWarrantyLabel(baseMonths + companyExtraMonths);
  if (bd.inBase) {
    const extras = bd.claimExtraMonths + bd.orgExtraMonths;
    if (extras > 0) {
      return `${label} standard warranty: ${bd.baseCountdown} remaining · +${extras} months extended after that`;
    }
    return `${label} warranty: ${bd.baseCountdown} remaining`;
  }

  return `Extended warranty: ${bd.totalCountdown} remaining`;
}

export function getWarrantyColor(status) {
  switch (status) {
    case 'within':  return colors.success;
    case 'warning': return colors.statusYellow;
    case 'outside': return colors.danger;
    default:        return colors.unknown;
  }
}

export const WARRANTY_RATIO_TARGET = 0.002; // 0.2%

export function getWarrantyRatioColor(ratio, target = WARRANTY_RATIO_TARGET) {
  if (ratio < target - 0.0003) return colors.success;    // below 0.17%
  if (ratio <= target)          return colors.statusYellow; // 0.17–0.2%
  return colors.danger;                                   // above 0.2%
}
